'use client'

import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { ConfigContext } from '@/components/common/ConfigContext'
import { ExtendedHotelInfo } from '@/lib/backend-api'
import { loadLikedHotels } from '@/lib/utils/utils'
import {
  SEARCH_PARAMETERS_UPDATED_EVENT_TYPE,
  SearchParametersUpdatedEvent,
} from '@/components/ui/wizard/Wizard'
import WrapperElement from '@/components/elements/WrapperElement'
import classNames from 'classnames'
import SearchResults, {
  SearchResultElement,
  SearchResultListType,
} from '@/components/ui/search/SearchResults'
import { DUMMY_SEARCH_RESULT } from '@/components/pages/search-page/SearchPage'
import SearchResultHeader, {
  PluralizedSearchResultHeaderMessage,
} from '@/components/pages/search-page/NumberOfResults'
import {
  fetchSearchResult,
  HOTEL_SEARCH_PAGE_SIZE,
  SearchPageSearchResult,
  trackUserAction,
} from '@/lib/frontent-api'
import { CLOSE_MAP_CLICK } from '@/lib/sharedConstants'
import MapModal from '@/components/ui/search/MapModal'
import { SearchPageContext } from '@/components/pages/search-page/SearchPageContext'
import { useOnMobileOrTablet } from '@/lib/hooks'
import { TrackingDataEventTypes } from '@/types/Misc'
import SearchFilterRemovalElement from '@/components/elements/SearchFilterRemovalElement'
import { Store } from '@reduxjs/toolkit'
import { Provider } from 'react-redux'
import { useTranslations } from 'next-intl'
import AppMap from '@/components/ui/search/AppMap'

interface SearchResultListProps {
  skipHeaderAndFooter: boolean
  bookmarksPage: boolean
}

function getMapItems(r: SearchPageSearchResult): ExtendedHotelInfo[] {
  return [...r.SearchResult.MapItems, ...r.BackfillResult?.MapItems]
}

function getSearchResultItems(
  r: SearchPageSearchResult,
  page: number,
  withBackfill: boolean,
): SearchResultListType {
  const maxPossibleNumberOfResults = page * HOTEL_SEARCH_PAGE_SIZE
  const lastSearchResultInCurrentResult =
    r.SearchResult.NumberOfResults < maxPossibleNumberOfResults &&
    r.SearchResult.NumberOfResults >= maxPossibleNumberOfResults - HOTEL_SEARCH_PAGE_SIZE

  const resultItems = r.SearchResult.SearchResultItems
  const backFillResultItems = withBackfill ? r.BackfillResult.SearchResultItems : []

  if (
    lastSearchResultInCurrentResult &&
    backFillResultItems.length > 0 &&
    r.SearchResult.NumberOfResults &&
    r.BackfillResult.NumberOfResults
  ) {
    return [...resultItems, { separator: true }, ...backFillResultItems]
  } else {
    return [...resultItems, ...backFillResultItems]
  }
}

export default function SearchResultList(props: SearchResultListProps): ReactElement {
  const config = useContext(ConfigContext)!
  const [searchResult, setSearchResult] = useState<SearchPageSearchResult | undefined>(undefined)
  const [searchResultItems, setSearchResultItems] = useState<SearchResultListType>([])
  const [mapItems, setMapItems] = useState<ExtendedHotelInfo[]>([])
  const [queryParams, setQueryParams] = useState<Record<string, string>>()
  const [page, setPage] = useState<number>(1)
  const [searchActive, setSearchActive] = useState(true)
  const { displayMap, setDisplayMap } = useContext(SearchPageContext)
  const onMobileOrTablet = useOnMobileOrTablet()
  const [likedHotels, setLikedHotels] = useState<string[]>([])
  const storeInitialized = useContext(SearchPageContext).storeInitialized
  const t = useTranslations('SearchPage')

  useEffect(() => {
    function eventListener(): void {
      if (likedHotels.join('') !== loadLikedHotels().join('')) {
        setLikedHotels(loadLikedHotels())
      }
    }

    if (props.bookmarksPage) {
      eventListener()

      window.addEventListener('storage', eventListener)
    }

    return () => window.removeEventListener('storage', eventListener)
  }, [])

  function addLikedHotels(params: Record<string, string>): void {
    if (props.bookmarksPage) {
      params['h'] = loadLikedHotels().join('_') || ''
      params['unavailable'] = '1'
    }
  }

  function searchParams(): Record<string, string> {
    const params = {
      ...queryParams,
    }
    addLikedHotels(params)

    return params
  }

  function trackSearchImpression(searchResult: SearchPageSearchResult) {
    const hotels = [
      ...searchResult.SearchResult.SearchResultItems,
      ...searchResult.BackfillResult.SearchResultItems,
    ]
    trackUserAction(config.domain, {
      type: TrackingDataEventTypes.HOTEL_IMPRESSION_SEARCH,
      hotelCodes: hotels.map((item) => item.HotelCode),
    })

    return searchResult
  }

  function runSearch(params: Record<string, string> | undefined, page: number): void {
    setSearchActive(true)
    void fetchSearchResult(config.domain, config.currency, page, params || {})
      .then((r) => {
        setSearchResult(r)
        setSearchActive(false)
        if (page === 1) {
          setMapItems(getMapItems(r))
          window.scrollTo(0, 0)
        }
        return r
      })
      .then(trackSearchImpression)
      .then((r) => appendSearchResult(getSearchResultItems(r, page, !props.bookmarksPage)))
      .catch((e: Error) => {
        if (e.name === 'AbortError') {
          return undefined
        }
        setSearchActive(false)

        return {}
      })
  }

  useEffect(() => {
    document.body.setAttribute('fullscreen', '')
    window.parent.postMessage(
      {
        message: 'full-screen-page-displayed',
      },
      '*',
    )
  }, [])

  useEffect(() => {
    function searchParametersUpdatedListener(e: SearchParametersUpdatedEvent): void {
      if (props.bookmarksPage && loadLikedHotels().length === 0) {
        return
      }

      setQueryParams(e.detail)
      setSearchResult(undefined)
      setSearchResultItems([])
      setPage(1)
      const params = { ...e.detail }
      addLikedHotels(params)
      runSearch(params, 1)
    }

    function closeMapListener(): void {
      setDisplayMap(false)
    }

    window.addEventListener(SEARCH_PARAMETERS_UPDATED_EVENT_TYPE, searchParametersUpdatedListener)
    window.addEventListener(CLOSE_MAP_CLICK, closeMapListener)

    return () => {
      window.removeEventListener(
        SEARCH_PARAMETERS_UPDATED_EVENT_TYPE,
        searchParametersUpdatedListener,
      )
      window.removeEventListener(CLOSE_MAP_CLICK, closeMapListener)
    }
  }, [])

  function loadMore(): void {
    setPage(page + 1)
    runSearch(searchParams(), page + 1)
  }

  function moreHotelSearchResultsAvailable(): boolean {
    if (!searchResult) {
      return true
    }

    return (
      searchResultItems.length <
      (!props.bookmarksPage
        ? searchResult.NumberOfResults
        : searchResult.SearchResult.NumberOfResults)
    )
  }

  function appendSearchResult(newSearchResult: SearchResultListType): void {
    if (!newSearchResult || newSearchResult.length === 0) {
      return
    }

    setSearchResultItems([...searchResultItems, ...newSearchResult])
  }

  function isEmptyBookmarksPage(): boolean {
    return props.bookmarksPage && likedHotels.length === 0
  }

  function getSearchResultMessage() {
    if (
      !searchResult?.SearchResult.NumberOfResults &&
      !searchResult?.BackfillResult.NumberOfResults
    ) {
      return t('resultMessageNoResult')
    } else if (
      !searchResult?.SearchResult.NumberOfResults &&
      searchResult?.BackfillResult.NumberOfResults
    ) {
      return t('resultMessageNoMatchingResult')
    } else {
      return <PluralizedSearchResultHeaderMessage numberOfResults={searchResult.SearchResult} />
    }
  }

  if (!storeInitialized) {
    return <></>
  }

  return (
    <Provider store={window.store!}>
      <div
        className={classNames('min-h-[calc(100vh_-_330px)] lg:min-h-[calc(100vh_-_179px)]', {
          'lg:pt-[180px]': !props.skipHeaderAndFooter && !props.bookmarksPage,
          'lg:pt-[174px]': !props.skipHeaderAndFooter && props.bookmarksPage,
          'lg:pt-[64px]': props.skipHeaderAndFooter,
        })}
      >
        <WrapperElement padding="md:px-[4px] lg:px-3.5">
          <div
            className={classNames('relative mb-2 h-full w-full px-4 wd:pb-3', {
              'mt-4': props.skipHeaderAndFooter,
            })}
          >
            <SearchResultHeader
              setDisplayMap={setDisplayMap}
              bookmarksPage={props.bookmarksPage}
              message={getSearchResultMessage()}
              searchActive={searchActive}
            />
            {searchResult && searchResult.NumberOfResults == 0 && (
              <Provider store={window.store as Store}>
                <SearchFilterRemovalElement />
              </Provider>
            )}
            {searchResult && searchResult.NumberOfResults > 0 && (
              <SearchResults
                searchResultItems={searchResultItems}
                message={searchResult.Message}
                loadMore={loadMore}
                currency={config.currency}
                moreHotelSearchResultsAvailable={moreHotelSearchResultsAvailable()}
                backFillSearchResultQuantity={searchResult.BackfillResult}
                bookmarksPage={props.bookmarksPage}
              />
            )}
            {!searchResult && !isEmptyBookmarksPage() && (
              <div
                className={classNames({
                  'mt-[29px] lg:mt-[48px]': !props.skipHeaderAndFooter && !props.bookmarksPage,
                  'mt-12': props.skipHeaderAndFooter,
                })}
              >
                {DUMMY_SEARCH_RESULT.map((r, index) => (
                  <SearchResultElement skeleton={true} {...r} key={`${r.hotelName[0]}${index}`} />
                ))}
              </div>
            )}
          </div>
          {displayMap && (
            <div
              id="map-container"
              className={classNames(
                'top:0 h-screen w-screen lg:relative lg:block lg:h-full lg:w-full',
                {
                  'fixed inset-0 z-40 block': displayMap,
                },
              )}
            >
              {displayMap && onMobileOrTablet && (
                <AppMap
                  foundHotels={mapItems}
                  currency={config.currency}
                  domain={config.domain}
                  searchActive={searchActive}
                  whitelabel={props.skipHeaderAndFooter}
                  config={config}
                  withNavigationControls={true}
                  bookmarksPage={props.bookmarksPage}
                />
              )}
            </div>
          )}
        </WrapperElement>
      </div>
      {!onMobileOrTablet && displayMap && (
        <MapModal
          visible={displayMap}
          searchActive={searchActive}
          whitelabel={false}
          foundHotels={mapItems}
          closeModal={() => setDisplayMap(false)}
        />
      )}
    </Provider>
  )
}
